import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import RecyclingOutlinedIcon from '@mui/icons-material/RecyclingOutlined';
import Co2OutlinedIcon from '@mui/icons-material/Co2Outlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import { useTranslation } from 'react-i18next';
import {
  dashboardBranchCardSx,
  dashboardBranchKpiGridSx,
  dashboardBranchKpiTileSx,
  dashboardBranchUtilityGridSx,
  dashboardBranchUtilityTileSx,
} from '../../constants/dashboard-layout';
import { dashboardMetricIconSx } from '../../constants/dashboard-card';
import type { EsgBranchMonthlyRow } from '../../types/esg-dashboard.type';
import type { EsgBranchesWidgetId } from '../../types/dashboard-customize.type';
import { UtilityCategory } from '../../types/utility-emission.type';
import { carbonMaterialLabel, formatTco2e } from '../../utils/carbonAvoidance.util';

type BranchSiteCardProps = {
  row: EsgBranchMonthlyRow;
  orgTrainingHours: number | null | undefined;
  formatSar: (amount: number) => string;
  visibleWidgets?: readonly EsgBranchesWidgetId[];
  /** Mark full card for multi-hole coachmark spotlight. */
  highlightHole?: boolean;
};

type KpiTileProps = {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  unit: string;
};

function KpiTile({ icon, label, value, unit }: KpiTileProps) {
  const theme = useTheme();
  return (
    <Box sx={dashboardBranchKpiTileSx(theme)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Box sx={dashboardMetricIconSx(theme, 'primary')}>{icon}</Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, lineHeight: 1.35 }}
          noWrap
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          {unit}
        </Typography>
      </Box>
    </Box>
  );
}

type UtilityTileProps = {
  icon: React.ReactNode;
  label: string;
  primary: string;
  primaryUnit: string;
  cost: string;
};

function UtilityTile({ icon, label, primary, primaryUnit, cost }: UtilityTileProps) {
  const theme = useTheme();
  return (
    <Box sx={dashboardBranchUtilityTileSx(theme)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box sx={{ ...dashboardMetricIconSx(theme, 'primary'), '& svg': { fontSize: 18 } }}>
          {icon}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, lineHeight: 1.35 }}
          noWrap
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'end', minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}
          noWrap
        >
          {primary}{' '}
          <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {primaryUnit}
          </Box>
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {cost}
        </Typography>
      </Box>
    </Box>
  );
}

const BranchSiteCard: React.FC<BranchSiteCardProps> = ({
  row,
  orgTrainingHours,
  formatSar,
  visibleWidgets,
  highlightHole = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const siteName = row.collection_site_name ?? row.collection_site_id;

  const utilityIcon = (category: UtilityCategory) => {
    if (category === UtilityCategory.WATER) return <WaterDropOutlinedIcon sx={{ fontSize: 18 }} />;
    if (category === UtilityCategory.ELECTRICITY) return <BoltOutlinedIcon sx={{ fontSize: 18 }} />;
    if (category === UtilityCategory.GAS) return <LocalGasStationOutlinedIcon sx={{ fontSize: 18 }} />;
    return <LocalFireDepartmentOutlinedIcon sx={{ fontSize: 18 }} />;
  };
  const show = (id: EsgBranchesWidgetId) =>
    !visibleWidgets || visibleWidgets.includes(id);

  const utilities = (row.scope_emissions?.activity_lines ?? []).map((line) => ({
    label: line.label,
    primary: line.consumption.toLocaleString(),
    primaryUnit: line.canonical_unit,
    cost: formatSar(line.amount_sar),
    icon: utilityIcon(line.category),
  }));

  return (
    <Paper
      elevation={0}
      {...(highlightHole ? { 'data-setup-hole': true } : {})}
      sx={dashboardBranchCardSx(theme)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box sx={dashboardMetricIconSx(theme, 'primary')}>
          <LocationOnOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
          {siteName}
        </Typography>
      </Box>

      <Box sx={dashboardBranchKpiGridSx}>
        {show('total_waste') && (
          <KpiTile
            icon={<RecyclingOutlinedIcon sx={{ fontSize: 20 }} />}
            label={t('dashboard.branch.totalWaste')}
            value={row.total_waste_diverted_kg.toLocaleString()}
            unit={t('units.kg')}
          />
        )}
        {show('carbon_avoided') && (
          <KpiTile
            icon={<Co2OutlinedIcon sx={{ fontSize: 20 }} />}
            label={t('dashboard.branch.carbonAvoided')}
            value={formatTco2e(row.total_avoided_tco2e ?? 0)}
            unit={t('units.tco2e')}
          />
        )}
        {show('fill_reports') && (
          <KpiTile
            icon={<AssessmentOutlinedIcon sx={{ fontSize: 20 }} />}
            label={t('dashboard.branch.fillReports')}
            value={row.fill_level_report_count.toLocaleString()}
            unit={t('dashboard.branch.reportsLabel')}
          />
        )}
        {show('scope_1') && (
          <KpiTile
            icon={<Co2OutlinedIcon sx={{ fontSize: 20 }} />}
            label="Scope 1"
            value={row.scope_emissions.scope_1_tco2e === null ? 'Incomplete' : formatTco2e(row.scope_emissions.scope_1_tco2e)}
            unit={t('units.tco2e')}
          />
        )}
        {show('scope_2') && (
          <KpiTile
            icon={<Co2OutlinedIcon sx={{ fontSize: 20 }} />}
            label="Scope 2"
            value={row.scope_emissions.scope_2_tco2e === null ? 'Incomplete' : formatTco2e(row.scope_emissions.scope_2_tco2e)}
            unit={t('units.tco2e')}
          />
        )}
        {show('training_hours') && (
          <KpiTile
            icon={<SchoolOutlinedIcon sx={{ fontSize: 20 }} />}
            label={t('dashboard.branch.trainingHours')}
            value={
              typeof orgTrainingHours === 'number' ? (
                orgTrainingHours.toFixed(1)
              ) : (
                <Box component="span" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                  —
                </Box>
              )
            }
            unit={t('units.hours')}
          />
        )}
      </Box>

      {show('carbon_by_stream') && (row.avoided_tco2e_by_stream?.length ?? 0) > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {row.avoided_tco2e_by_stream.map((stream) => (
            <Chip
              key={stream.material_label}
              size="small"
              label={`${carbonMaterialLabel(stream.material_label)} · ${formatTco2e(stream.total_avoided_tco2e)} tCO2e`}
              sx={{
                fontWeight: 500,
                bgcolor: 'background.elevated',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          ))}
        </Box>
      ) : null}

      {show('utilities') && (
        <Box>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: 'text.secondary',
              mb: 1,
            }}
          >
            {t('dashboard.utilitiesSection')}
          </Typography>
          <Box sx={dashboardBranchUtilityGridSx}>
            {utilities.map((u) => (
              <UtilityTile
                key={u.label}
                icon={u.icon}
                label={u.label}
                primary={u.primary}
                primaryUnit={u.primaryUnit}
                cost={u.cost}
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default BranchSiteCard;
